import { InfoOutlined } from '@material-ui/icons';
import React from 'react';

import { useEscapeKey } from '../../../../utils/useEscapeKey';
import { useCanViewerEditApplication } from '../../useCanViewerEditApplication';

interface BackNavigationPopupProps {
  /** Whether the popup is visible. */
  displayModal: boolean;
  /** Acknowledge and stay on the quote. */
  handleClose: () => void;
  /**
   * NCP-5642: whether the viewer is an internal (Foxquilt) broker, so the copy
   * only names "Edit" when this viewer can see it.
   */
  isInternalUser: boolean;
  /**
   * NCP-5649: whether the quote is New Business — the only journey whose
   * policy card renders "Edit" (NCP-5396), so the copy must not name it on
   * an endorsement or any other transaction.
   */
  isNewBusiness: boolean;
}

/**
 * Friendly reminder shown when the applicant presses the browser's Back button
 * on the quote page (NCP-5230). Back is absorbed by useBlockBrowserBack, so
 * without this the press would look like a broken button — the copy points them
 * at the "Edit" button on the policy card when this viewer has one
 * (enableApplicationEdit, NCP-5437; restrictApplicationEditToInternal,
 * NCP-5642), and at the quote itself otherwise.
 */
const BackNavigationPopup: React.FC<BackNavigationPopupProps> = ({
  displayModal,
  handleClose,
  isInternalUser,
  isNewBusiness
}) => {
  useEscapeKey(handleClose, displayModal);

  // The copy must only name the policy card's "Edit" button when this viewer
  // actually has it, so it applies the same two gates as Coverages: the
  // shared Configuration hook (same cache-first query, so a cache hit, not a
  // second call) and the New Business journey gate, which the hook leaves to
  // its caller.
  const canViewerEdit = useCanViewerEditApplication(isInternalUser);
  const canEditApplication = canViewerEdit && isNewBusiness;

  // Unmounted rather than hidden (unlike the sibling quote popups): this popup
  // is rendered by Quote.tsx itself, so leaving its heading in the DOM while
  // closed would put an <h2> ahead of the page's own heading hierarchy.
  if (!displayModal) {
    return null;
  }

  return (
    <div
      data-testid="back-navigation-popup"
      className="fixed w-screen h-screen top-0 left-0 bg-black bg-opacity-50 z-50"
      onClick={handleClose}
    >
      <div className="flex h-screen justify-center overflow-auto">
        <div
          className="relative max-w-xl flex flex-col h-fit-content bg-white border-2 border-white rounded-lg self-center my-8 mx-2 md:mx-0 p-6 md:p-10 text-center z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-center mb-4">
            <InfoOutlined style={{ fontSize: '4rem' }} />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Need to change an answer?
          </h2>
          <p className="text-base md:text-lg leading-relaxed mb-8">
            {canEditApplication
              ? 'Use the "Edit" button on the quote in order to make changes to your insurance application.'
              : 'Use the quote in order to make changes to your insurance application.'}
          </p>
          <button
            data-testid="back-navigation-popup-acknowledge"
            className="w-full bg-tertiary hover:bg-tertiary-dark border-tertiary-dark rounded-md uppercase pointer font-bold px-14 py-3 text-mobileFont md:text-xl text-white"
            onClick={handleClose}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default BackNavigationPopup;
